// / <reference types="@types/googlemaps" />
import {AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {Geoposition} from '@awesome-cordova-plugins/geolocation/ngx';
import {Geolocation} from '@awesome-cordova-plugins/geolocation/ngx';
import {MenuController, ModalController} from '@ionic/angular';
import {Subscription} from 'rxjs';
import {tap} from 'rxjs/operators';
import {Step} from '../../../../core/api/resources/step.resource';
import {GpxParserService} from '../../../../services/gpx-parser/gpx-parser.service';

@Component({
  selector: 'unr-google-map',
  templateUrl: 'google-map.component.html',
  styleUrls: [
    'google-map.component.scss'
  ]
})
export class UnrGoogleMapComponent implements AfterViewInit, OnDestroy {
  @ViewChild('googleMap', {static: false}) googleMapElement: ElementRef;
  @Input() gpxUrl: string;
  @Input() step: Step;

  googleMap: any;
  userMarker: any;
  initialCoordinates: any;
  userLocationSubscription: Subscription;
  backdrop;

  constructor(private geoLocation: Geolocation,
              private gpx: GpxParserService,
              private modalCtrl: ModalController,
              private menuController: MenuController,
              private router: Router) {
  }

  ionViewWillEnter(): void {
    if (!google) {
      this.router.navigate(['no-connection']);
    }
  }

  ngAfterViewInit(): void {
    this.menuController.enable(false, 'sideMenu').then();
    setTimeout(() => {
      this.addBackdropDismissClick();
      this.initMap();
      this.subscribeToUserLocation();
      this.drawRoute();
    }, 0);
  }

  ngOnDestroy(): void {
    this.backdrop && this.backdrop.removeAttribute('onclick');
    this.userLocationSubscription && this.userLocationSubscription.unsubscribe();
    this.menuController.enable(true, 'sideMenu').then();
  }

  private drawRoute(): void {
    this.gpx.getGpxFile(this.gpxUrl)
      .subscribe(gpx => {
        const routePoints: Array<google.maps.LatLngLiteral> = this.gpx.geTrackPoints(gpx);

        this.drawBeginPoint(routePoints);
        this.drawEndPoint(routePoints);
        this.drawRouteLines(routePoints);
        this.drawStepPoint();

        if (!this.step) {
          this.centerRoute(routePoints);
        } else if (!this.step.longitude
          || !this.step.latitude) {
          this.centerRoute(routePoints);
        }
      });
  }

  private drawStepPoint(): void {
    if (this.step) {
      const position: google.maps.LatLngLiteral = {
        lng: Number(this.step.longitude),
        lat: Number(this.step.latitude)
      };

      const stepMarker: google.maps.Marker = new google.maps.Marker({
        position,
        map: this.googleMap,
        animation: google.maps.Animation.DROP,
        title: 'Step point',
        icon: {
          url: 'https://www.mofit.be/step-marker.png'
        }
      });
      this.googleMap.setCenter(position);
      this.googleMap.setZoom(12);
    }
  }

  private drawRouteLines(routePoints: Array<google.maps.LatLngLiteral>): void {
    const routePath: google.maps.Polyline = new google.maps.Polyline({
      path: routePoints,
      geodesic: true,
      strokeColor: '#FFA214',
      strokeOpacity: 1.0,
      strokeWeight: 4
    });
    routePath.setMap(this.googleMap);
  }

  private centerRoute(routePoints: Array<google.maps.LatLngLiteral>): void {
    const bounds: google.maps.LatLngBounds = new google.maps.LatLngBounds();
    routePoints.forEach(point => bounds.extend(point));
    this.googleMap.fitBounds(bounds);
    this.googleMap.setCenter(bounds.getCenter());
  }

  private drawEndPoint(routePoints: Array<google.maps.LatLngLiteral>): void {
    const endMarker: google.maps.Marker = new google.maps.Marker({
      position: routePoints[routePoints.length - 1],
      map: this.googleMap,
      animation: google.maps.Animation.DROP,
      title: 'End point',
      icon: {
        url: 'https://www.mofit.be/unr.png'
      }
    });
  }

  private drawBeginPoint(routePoints: Array<google.maps.LatLngLiteral>): void {
    const beginMarker: google.maps.Marker = new google.maps.Marker({
      position: routePoints[0],
      map: this.googleMap,
      animation: google.maps.Animation.DROP,
      title: 'Begin point',
      icon: {
        url: 'https://maps.google.com/mapfiles/ms/icons/orange-dot.png'
      }
    });
  }

  private subscribeToUserLocation(): void {
    if (!this.googleMap) {
      return;
    }

    this.userLocationSubscription = this.geoLocation
      .watchPosition()
      .pipe(
        tap((geoPosition: Geoposition) => {
          if (geoPosition.coords) {
            const latLng: google.maps.LatLngLiteral = {
              lat: geoPosition.coords.latitude,
              lng: geoPosition.coords.longitude
            };

            this.userMarker
              ? this.userMarker.setPosition(latLng)
              : this.createNewUserLocationMarker(latLng);
          }
        })
      )
      .subscribe(
        next => console.log('Received user position'),
        err => console.log('Error retrieving user position', err)
      );
  }

  private createNewUserLocationMarker(latLng: google.maps.LatLngLiteral): void {
    this.userMarker = new google.maps.Marker({
      position: latLng,
      map: this.googleMap,
      animation: google.maps.Animation.DROP,
      title: 'Your current position',
      icon: {
        scaledSize: new google.maps.Size(30, 30),
        url: 'https://www.mofit.be/car.png'
      }
    });
  }

  private initMap(): void {
    this.initialCoordinates = new google.maps.LatLng(50.850346, 4.351721);

    const mapOptions: google.maps.MapOptions = {
      center: this.initialCoordinates,
      zoom: 5,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      streetViewControl: false,
      fullscreenControl: false,
      mapTypeControl: false,
      clickableIcons: false
    };

    this.googleMap = new google.maps.Map(this.googleMapElement.nativeElement, mapOptions);
  }

  private addBackdropDismissClick(): void {
    this.backdrop = window.document.querySelector('.modal-wrapper');
    if (this.backdrop) {
      this.backdrop.onclick = () => {
        this.modalCtrl.dismiss();
      };
    }
  }
}
